'use client';

import { useMemo } from 'react';
import Disclosure from './Disclosure';
import { useConsumedDisclosures, useDisclosureContext, useDisclosureNumberingMap } from 'contexts/DisclosureProvider';
import { AnalyticsModuleTrackingProvider } from 'components/analytics/AnalyticsModuleTracking';

/**
 * Renders the page's footer disclosures, filtering out any consumed by DisclosureAddOn modules.
 */
const DynamicDisclosures = () => {
  const pageDisclosures = useDisclosureContext().getSortedDisclosures();
  const { consumedDisclosureIds } = useConsumedDisclosures();
  const disclosureNumberingMap = useDisclosureNumberingMap();

  // Filter out disclosures that have been consumed by DisclosureAddOn modules
  const disclosures = useMemo(() => {
    return pageDisclosures.filter(disclosure => {
      const disclosureId = disclosure.sys?.id;
      if (!disclosureId) return true; // Keep disclosures without IDs
      return !consumedDisclosureIds.has(disclosureId);
    });
  }, [pageDisclosures, consumedDisclosureIds]);

  if (!disclosures.length) return null;

  return (
    <AnalyticsModuleTrackingProvider
      initialAttributes={{
        moduleType: 'dynamic-disclosures',
        moduleTitle: 'Dynamic Disclosures',
        moduleIndex: '1',
        // Detects the browser at runtime; there is no value to parse at a boundary here.
        // oxlint-disable-next-line anti-slop/no-runtime-typeof
        pageTitle: typeof document !== 'undefined' ? document.title : '',
      }}>
      <div className="mb-2 last:mb-0 dynamic-disclosures">
        <div className="content-wrapper">
          <div className="flex flex-col gap-y-2">
            {disclosures.map((disclosure, index) => {
              // Get auto number from the global numbering map to maintain consistency
              const disclosureId = disclosure.sys?.id;
              const autoNumber =
                !disclosure.symbol && disclosureId ? disclosureNumberingMap.get(disclosureId) : undefined;

              return <Disclosure key={disclosureId || index} {...disclosure} autoNumber={autoNumber} />;
            })}
          </div>
        </div>
      </div>
    </AnalyticsModuleTrackingProvider>
  );
};

export default DynamicDisclosures;
